import moment from 'moment';
import i18n from '@/i18n';
import {
  CouponDetail,
  CouponFilterType,
  CouponReceiveResult,
  CouponTab,
  CouponType,
  ExperienceCouponOption,
  ExperienceOpenPreview,
  ExperienceOpenResult,
  ExperienceOrderPage,
  RewardHistoryItem,
  TaskBelong,
  TaskDetail,
  WelfareCouponItem,
  WelfareTask,
} from './types';

const future = (days: number) => moment().add(days, 'days').valueOf();
const past = (days: number) => moment().subtract(days, 'days').valueOf();

const MOCK_EP_SYMBOLS = ['BTC', 'ETH', 'ETC', 'SOL', 'LTC', 'BNB', 'FIL', 'DOGE', 'XRP', 'ADA'];

/** 合约选券 / 我的卡券共用的仓位体验券 mock */
export const MOCK_POSITION_EXPERIENCE_COUPONS: ExperienceCouponOption[] = [
  {
    userCouponId: 1011,
    amount: '100',
    coin: 'USDT',
    leverage: 200,
    positionValidHours: 24,
    expireTime: moment('2026-12-24 08:00:00').valueOf(),
    symbols: MOCK_EP_SYMBOLS,
  },
  {
    userCouponId: 1012,
    amount: '100',
    coin: 'USDT',
    leverage: 25,
    positionValidHours: 24,
    expireTime: moment('2026-12-24 08:00:00').valueOf(),
    symbols: MOCK_EP_SYMBOLS,
  },
  {
    userCouponId: 1013,
    amount: '50',
    coin: 'USDT',
    leverage: 100,
    positionValidHours: 24,
    expireTime: moment('2026-11-30 08:00:00').valueOf(),
    symbols: ['BTC', 'ETH', 'SOL'],
  },
  {
    userCouponId: 1014,
    amount: '200',
    coin: 'USDT',
    leverage: 50,
    positionValidHours: 48,
    expireTime: moment('2026-12-24 08:00:00').valueOf(),
    symbols: ['BTC', 'ETH', 'ETC', 'SOL'],
  },
  {
    userCouponId: 1015,
    amount: '20',
    coin: 'USDT',
    leverage: 10,
    positionValidHours: 12,
    expireTime: moment('2026-09-30 08:00:00').valueOf(),
    symbols: ['BTC'],
  },
];

const toWelfarePositionCoupon = (
  item: ExperienceCouponOption,
  extra?: Partial<WelfareCouponItem>,
): WelfareCouponItem => ({
  userCouponId: item.userCouponId,
  amount: Number(item.amount),
  coin: item.coin,
  // 不在模块加载时写死 i18n 文案，由页面 getCouponTitle / t(couponTypeI18nKey) 映射
  couponType: 'POSITION_EXPERIENCE',
  couponFamily: 'POSITION_EXPERIENCE',
  validDays: 30,
  deductRate: null,
  positionValidHours: item.positionValidHours,
  leverage: item.leverage,
  symbols: item.symbols,
  expireTime: item.expireTime,
  allowedActions: ['USE', 'DETAIL'],
  buttonCode: 'USE',
  ...extra,
});

const makeTiers = (
  values: Array<{ target: string; amount: string; type?: CouponType; status?: 'LOCKED' | 'ISSUED' | 'EXPIRED' }>,
) =>
  values.map((item, index) => ({
    tierNo: index + 1,
    targetValue: item.target,
    couponAmount: item.amount,
    amount: item.amount,
    coin: 'USDT',
    couponType: item.type || ('EXPERIENCE' as CouponType),
    rewardStatus: item.status || ('LOCKED' as const),
    userCouponId: item.status === 'ISSUED' ? 8000 + index : null,
  }));

const couponTypeKey = (type: CouponType) => `welfareCenter.couponType.${type}`;

const buildTaskData = (): Record<TaskBelong, WelfareTask[]> => ({
  NEW_USER_TASK: [
    {
      taskId: 1001,
      taskTitle: i18n.t('welfareCenter.mock.tasks.register.title'),
      taskBrief: i18n.t('welfareCenter.mock.tasks.register.brief'),
      couponAmount: 50,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 2 * 86400 + 9 * 3600 + 56 * 60 + 20,
      actionCode: 'REGISTER',
      actionButtonText: i18n.t('welfareCenter.action.register'),
      taskType: 'SINGLE',
      taskLevel: 'BASIC',
      taskStatus: 'IN_PROGRESS',
      rewardTiers: makeTiers([{ target: '1', amount: '50', type: 'EXPERIENCE' }]),
    },
    {
      taskId: 1002,
      taskTitle: i18n.t('welfareCenter.mock.tasks.firstTrade.title'),
      taskBrief: i18n.t('welfareCenter.mock.tasks.firstTrade.brief'),
      couponAmount: 50,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 2 * 86400 + 9 * 3600,
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.trade'),
      taskType: 'SINGLE',
      taskLevel: 'BASIC',
      taskStatus: 'IN_PROGRESS',
      progressCurrent: '25.00',
      progressTarget: '50.00',
      progressText: '25.00/50.00',
      rewardTiers: makeTiers([{ target: '50', amount: '50', type: 'EXPERIENCE' }]),
    },
    {
      taskId: 1003,
      taskTitle: '完成首次充值',
      taskBrief: '充值满 100 USDT 即可领取奖励',
      couponAmount: 30,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.FEE_DEDUCT'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 3 * 86400,
      actionCode: 'DEPOSIT',
      actionButtonText: i18n.t('welfareCenter.action.deposit', { defaultValue: '去充值' }),
      taskType: 'SINGLE',
      taskLevel: 'BASIC',
      taskStatus: 'IN_PROGRESS',
      progressCurrent: '20.00',
      progressTarget: '100.00',
      progressText: '充值 20/100USDT',
      rewardTiers: makeTiers([{ target: '100', amount: '30', type: 'FEE_DEDUCT' }]),
    },
    {
      taskId: 1004,
      taskTitle: '新手任务（已完成）',
      taskBrief: '已完成任务，可查看已发放卡券',
      couponAmount: 20,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 0,
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.completed'),
      taskType: 'SINGLE',
      taskLevel: 'BASIC',
      taskStatus: 'COMPLETED',
      progressCurrent: '100.00',
      progressTarget: '100.00',
      rewardTiers: makeTiers([{ target: '100', amount: '20', type: 'EXPERIENCE', status: 'ISSUED' }]),
    },
    {
      taskId: 1005,
      taskTitle: '新手任务（已过期）',
      taskBrief: '任务已过期，仅作样式参考',
      couponAmount: 10,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 0,
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.expired'),
      taskType: 'SINGLE',
      taskLevel: 'BASIC',
      taskStatus: 'EXPIRED',
      progressCurrent: '0',
      progressTarget: '100.00',
      rewardTiers: makeTiers([{ target: '100', amount: '10', type: 'EXPERIENCE', status: 'EXPIRED' }]),
    },
    {
      taskId: 1101,
      taskTitle: i18n.t('welfareCenter.mock.tasks.checkin.title'),
      taskBrief: i18n.t('welfareCenter.mock.tasks.checkin.brief'),
      couponAmount: 120,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 12 * 86400 + 8 * 3600 + 20 * 60 + 58,
      expireTime: future(12),
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.trade'),
      taskType: 'CHECK_IN',
      taskLevel: 'ADVANCED',
      taskStatus: 'IN_PROGRESS',
      progressCurrent: '2500.00',
      progressTarget: '5000.00',
      checkinProgress: {
        currentStreakDays: 3,
        maxStreakDays: 14,
        todayCompleted: false,
        todayTradeAmount: '2500.00',
        todayTargetAmount: '5000.00',
      },
      rewardTiers: makeTiers([
        { target: '1', amount: '10', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '2', amount: '10', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '3', amount: '10', type: 'FEE_DEDUCT' },
        { target: '5', amount: '15', type: 'EXPERIENCE' },
        { target: '7', amount: '20', type: 'POSITION_EXPERIENCE' },
        { target: '10', amount: '25', type: 'PNL_DEDUCT' },
        { target: '14', amount: '30', type: 'EXPERIENCE' },
      ]),
    },
    {
      taskId: 1102,
      taskTitle: i18n.t('welfareCenter.mock.tasks.cumulative.title'),
      taskBrief: i18n.t('welfareCenter.mock.tasks.cumulative.brief'),
      couponAmount: 560,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 25 * 86400 + 8 * 3600 + 20 * 60 + 58,
      expireTime: future(25),
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.trade'),
      taskType: 'CUMULATIVE',
      taskLevel: 'ADVANCED',
      taskStatus: 'IN_PROGRESS',
      progressCurrent: '162293.03',
      progressTarget: '10000000',
      progressValue: '162293.03',
      rewardTiers: makeTiers([
        { target: '50000', amount: '10', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '100000', amount: '10', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '300000', amount: '20', type: 'FEE_DEDUCT' },
        { target: '500000', amount: '20', type: 'EXPERIENCE' },
        { target: '1000000', amount: '30', type: 'POSITION_EXPERIENCE' },
        { target: '2000000', amount: '50', type: 'PNL_DEDUCT' },
        { target: '3000000', amount: '50', type: 'EXPERIENCE' },
        { target: '5000000', amount: '80', type: 'FEE_DEDUCT' },
        { target: '8000000', amount: '100', type: 'EXPERIENCE' },
        { target: '10000000', amount: '150', type: 'POSITION_EXPERIENCE' },
      ]),
    },
  ],
  ADVANCED_TASK: [
    {
      taskId: 2001,
      taskTitle: i18n.t('welfareCenter.mock.tasks.advancedTrade.title'),
      taskBrief: i18n.t('welfareCenter.mock.tasks.advancedTrade.brief'),
      couponAmount: 30,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 7 * 86400,
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.trade'),
      taskType: 'SINGLE',
      taskLevel: 'ADVANCED',
      taskStatus: 'IN_PROGRESS',
      progressCurrent: '3200.00',
      progressTarget: '10000.00',
      progressText: '3200.00/10000.00',
      rewardTiers: makeTiers([{ target: '10000', amount: '30', type: 'EXPERIENCE' }]),
    },
    {
      taskId: 2002,
      taskTitle: '进阶打卡赢好礼',
      taskBrief: '连续交易打卡，解锁阶梯奖励',
      couponAmount: 80,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 9 * 86400,
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.trade'),
      taskType: 'CHECK_IN',
      taskLevel: 'ADVANCED',
      taskStatus: 'IN_PROGRESS',
      progressCurrent: '1800.00',
      progressTarget: '3000.00',
      checkinProgress: {
        currentStreakDays: 5,
        maxStreakDays: 14,
        todayCompleted: true,
        todayTradeAmount: '3000.00',
        todayTargetAmount: '3000.00',
      },
      rewardTiers: makeTiers([
        { target: '1', amount: '5', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '3', amount: '10', type: 'FEE_DEDUCT', status: 'ISSUED' },
        { target: '5', amount: '10', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '7', amount: '15', type: 'PNL_DEDUCT' },
        { target: '10', amount: '20', type: 'EXPERIENCE' },
        { target: '14', amount: '20', type: 'POSITION_EXPERIENCE' },
      ]),
    },
    {
      taskId: 2003,
      taskTitle: '累计交易赢大奖',
      taskBrief: '合约累计交易量达标即可领取对应奖励',
      couponAmount: 400,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 20 * 86400,
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.trade'),
      taskType: 'CUMULATIVE',
      taskLevel: 'ADVANCED',
      taskStatus: 'IN_PROGRESS',
      progressCurrent: '162293.03',
      progressValue: '162293.03',
      rewardTiers: makeTiers([
        { target: '20000', amount: '5', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '50000', amount: '10', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '100000', amount: '10', type: 'FEE_DEDUCT', status: 'ISSUED' },
        { target: '300000', amount: '20', type: 'EXPERIENCE' },
        { target: '500000', amount: '20', type: 'PNL_DEDUCT' },
        { target: '1000000', amount: '30', type: 'POSITION_EXPERIENCE' },
        { target: '3000000', amount: '50', type: 'EXPERIENCE' },
        { target: '5000000', amount: '80', type: 'FEE_DEDUCT' },
        { target: '10000000', amount: '150', type: 'EXPERIENCE' },
      ]),
    },
  ],
  LIMITED_TIME_TASK: [
    {
      taskId: 3001,
      taskTitle: i18n.t('welfareCenter.mock.tasks.weekend.title'),
      taskBrief: i18n.t('welfareCenter.mock.tasks.weekend.brief'),
      couponAmount: 25,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 2 * 86400 + 6 * 3600,
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.trade'),
      taskType: 'SINGLE',
      taskStatus: 'IN_PROGRESS',
      rewardTiers: makeTiers([{ target: '1', amount: '25' }]),
    },
    {
      taskId: 3002,
      taskTitle: '限时累计交易冲刺',
      taskBrief: '限时活动期间完成累计交易量目标',
      couponAmount: 200,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 1 * 86400 + 12 * 3600,
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.trade'),
      taskType: 'CUMULATIVE',
      taskStatus: 'IN_PROGRESS',
      progressCurrent: '80000',
      progressValue: '80000',
      rewardTiers: makeTiers([
        { target: '50000', amount: '20', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '100000', amount: '30', type: 'FEE_DEDUCT' },
        { target: '300000', amount: '50', type: 'EXPERIENCE' },
        { target: '500000', amount: '100', type: 'POSITION_EXPERIENCE' },
      ]),
    },
    {
      taskId: 3003,
      taskTitle: '限时打卡活动',
      taskBrief: '连续 7 天打卡领取限时奖励',
      couponAmount: 70,
      coin: 'USDT',
      rewardLabel: i18n.t('welfareCenter.couponType.EXPERIENCE'),
      couponTag: i18n.t('welfareCenter.mock.couponTag'),
      countdownSeconds: 4 * 86400,
      actionCode: 'TRADE',
      actionButtonText: i18n.t('welfareCenter.action.trade'),
      taskType: 'CHECK_IN',
      taskStatus: 'IN_PROGRESS',
      progressCurrent: '1000',
      progressTarget: '2000',
      checkinProgress: {
        currentStreakDays: 2,
        maxStreakDays: 7,
        todayCompleted: false,
        todayTradeAmount: '1000',
        todayTargetAmount: '2000',
      },
      rewardTiers: makeTiers([
        { target: '1', amount: '10', type: 'EXPERIENCE', status: 'ISSUED' },
        { target: '3', amount: '20', type: 'FEE_DEDUCT' },
        { target: '5', amount: '20', type: 'EXPERIENCE' },
        { target: '7', amount: '20', type: 'POSITION_EXPERIENCE' },
      ]),
    },
  ],
});

const couponData: Record<CouponTab, WelfareCouponItem[]> = {
  WAIT_USE: [
    {
      userCouponId: 1001,
      amount: 50,
      coin: 'USDT',
      couponType: 'EXPERIENCE',
      validDays: 30,
      deductRate: 33,
      selfFundThreshold: '50.00',
      showSelfFundThreshold: true,
      expireTime: future(30),
      allowedActions: ['USE', 'DETAIL'],
      buttonCode: 'USE',
      status: 'WAIT_USE',
      statusDesc: '待使用',
    },
    {
      userCouponId: 1002,
      amount: 50,
      coin: 'USDT',
      couponType: 'FEE_DEDUCT',
      validDays: 30,
      deductRate: 33,
      expireTime: future(30),
      allowedActions: ['USE', 'DETAIL'],
      buttonCode: 'USE',
    },
    {
      userCouponId: 1003,
      amount: 50,
      coin: 'USDT',
      couponType: 'PNL_DEDUCT',
      validDays: 30,
      deductRate: 33,
      expireTime: future(30),
      allowedActions: ['USE', 'DETAIL'],
      buttonCode: 'USE',
    },
    {
      userCouponId: 1004,
      amount: 5,
      coin: 'LTC',
      couponType: 'TOKEN',
      validDays: 30,
      deductRate: null,
      expireTime: future(30),
      allowedActions: ['USE', 'DETAIL'],
      buttonCode: 'USE',
    },
    ...MOCK_POSITION_EXPERIENCE_COUPONS.map(item => toWelfarePositionCoupon(item)),
    {
      userCouponId: 1006,
      amount: 20,
      coin: 'USDT',
      couponType: 'EXPERIENCE',
      validDays: 15,
      deductRate: 50,
      expireTime: future(15),
      allowedActions: ['USE', 'DETAIL'],
      buttonCode: 'USE',
    },
    {
      userCouponId: 1007,
      amount: 30,
      coin: 'USDT',
      couponType: 'FEE_DEDUCT',
      validDays: 20,
      deductRate: 20,
      expireTime: future(20),
      allowedActions: ['USE', 'DETAIL'],
      buttonCode: 'USE',
    },
    {
      userCouponId: 1008,
      amount: 40,
      coin: 'USDT',
      couponType: 'EXPERIENCE',
      validDays: 10,
      deductRate: 33,
      expireTime: future(10),
      allowedActions: ['NONE'],
      buttonCode: 'NONE',
    },
  ],
  USED: [
    {
      userCouponId: 2001,
      amount: 50,
      coin: 'USDT',
      couponType: 'EXPERIENCE',
      validDays: 30,
      deductRate: 33,
      expireTime: future(20),
      allowedActions: ['USED', 'DETAIL'],
      buttonCode: 'USED',
      status: 'USING',
    },
    {
      userCouponId: 2002,
      amount: 20,
      coin: 'USDT',
      couponType: 'FEE_DEDUCT',
      validDays: 30,
      deductRate: 33,
      expireTime: future(12),
      allowedActions: ['USED'],
      buttonCode: 'USED',
    },
    toWelfarePositionCoupon(
      {
        userCouponId: 2011,
        amount: '100',
        coin: 'USDT',
        leverage: 200,
        positionValidHours: 24,
        expireTime: moment('2026-12-24 08:00:00').valueOf(),
        symbols: MOCK_EP_SYMBOLS,
      },
      { allowedActions: ['USED', 'DETAIL'], buttonCode: 'USED', status: 'USING' },
    ),
  ],
  EXPIRED: [
    {
      userCouponId: 3001,
      amount: 10,
      coin: 'USDT',
      couponType: 'PNL_DEDUCT',
      validDays: 14,
      deductRate: 20,
      expireTime: past(8),
      allowedActions: ['EXPIRED'],
      buttonCode: 'EXPIRED',
    },
    toWelfarePositionCoupon(
      {
        userCouponId: 3011,
        amount: '50',
        coin: 'USDT',
        leverage: 25,
        positionValidHours: 24,
        expireTime: past(3),
        symbols: ['BTC', 'ETH', 'SOL'],
      },
      { validDays: 14, allowedActions: ['EXPIRED'], buttonCode: 'EXPIRED' },
    ),
    {
      userCouponId: 3002,
      amount: 5,
      coin: 'SOL',
      couponType: 'TOKEN',
      validDays: 7,
      expireTime: past(2),
      allowedActions: ['EXPIRED'],
      buttonCode: 'EXPIRED',
    },
  ],
};

export const getMockTasks = (taskBelong: TaskBelong = 'NEW_USER_TASK') =>
  buildTaskData()[taskBelong] || [];

export const getMockTaskDetail = (taskId: number): TaskDetail => {
  const taskData = buildTaskData();
  const task = Object.keys(taskData)
    .reduce<WelfareTask[]>((all, key) => all.concat(taskData[key as TaskBelong]), [])
    .find(item => item.taskId === taskId);
  const taskTitle = task?.taskTitle || i18n.t('welfareCenter.mock.specifiedTask');
  return {
    taskId,
    title: task?.taskTitle || i18n.t('welfareCenter.mock.taskDetailTitle'),
    brief: task?.taskBrief || i18n.t('welfareCenter.mock.taskDetailBrief'),
    ruleDesc: i18n.t(`welfareCenter.mock.ruleDesc.${task?.taskType || 'SINGLE'}`, {
      defaultValue: i18n.t('welfareCenter.mock.ruleDesc', { taskTitle }),
      taskTitle,
    }),
    taskType: task?.taskType,
    rewardTiers: task?.rewardTiers,
    checkinProgress: task?.checkinProgress,
    progressCurrent: task?.progressCurrent,
    progressTarget: task?.progressTarget,
    progressValue: task?.progressValue,
  };
};

export const getMockCoupons = (tab: CouponTab = 'WAIT_USE', couponType?: CouponFilterType) => {
  const list = couponData[tab] || [];
  if (!couponType || couponType === 'ALL') return list;
  return list.filter(item => item.couponType === couponType);
};

export const getMockCouponDetail = (userCouponId: number): CouponDetail => {
  const coupon = Object.keys(couponData)
    .reduce<WelfareCouponItem[]>((all, key) => all.concat(couponData[key as CouponTab]), [])
    .find(item => item.userCouponId === userCouponId);
  return {
    amount: coupon?.amount || 10,
    coin: coupon?.coin || 'USDT',
    couponFamilyName: coupon?.couponFamilyName || coupon?.couponFamilyDesc,
    couponName: coupon?.couponName,
    couponType: coupon?.couponType,
    couponTypeDesc: coupon?.couponTypeDesc
      || (coupon?.couponType
        ? i18n.t(couponTypeKey(coupon.couponType as CouponType))
        : i18n.t('welfareCenter.couponType.EXPERIENCE')),
    couponFamily: coupon?.couponFamily,
    validDays: coupon?.validDays || 7,
    deductRate: coupon?.deductRate ?? null,
    selfFundThreshold: coupon?.selfFundThreshold,
    showSelfFundThreshold: coupon?.showSelfFundThreshold,
    positionValidDays: coupon?.positionValidDays,
    positionValidHours: coupon?.positionValidHours,
    leverage: coupon?.leverage,
    symbols: coupon?.symbols || [],
  };
};

export const getMockReceiveResult = (
  userCouponId: number,
  options?: { confirmConflict?: boolean; confirmToken?: string },
): CouponReceiveResult => {
  const confirmConflict = Boolean(options?.confirmConflict);
  const coupon = getMockCoupons('WAIT_USE').find(item => item.userCouponId === userCouponId);

  if (userCouponId === 1006 && !confirmConflict) {
    return {
      resultCode: 'RATE_CONFLICT_NEED_CONFIRM',
      conflictKind: 'SAME_FAMILY_RATE',
      couponFamily: 'EXPERIENCE',
      deductRate: 50,
      confirmToken: 'mock-rate-token-001',
      conflicts: [
        {
          couponFamily: 'EXPERIENCE',
          couponTypes: ['NORMAL_EXPERIENCE'],
          cycleId: 205001,
          deductRate: 33,
          cycleRemainingAmount: 20,
          expireTime: Date.now() + 86400000,
        },
      ],
      amount: coupon?.amount,
      coin: coupon?.coin,
    };
  }
  if (userCouponId === 1007 && !confirmConflict) {
    return {
      resultCode: 'FAMILY_CONFLICT_NEED_CONFIRM',
      conflictKind: 'FAMILY_EXCLUSIVE',
      couponFamily: 'EXPERIENCE',
      deductRate: 20,
      confirmToken: 'mock-family-token-001',
      conflicts: [
        {
          couponFamily: 'FEE_DEDUCT',
          couponTypes: ['NORMAL_FEE_DEDUCT'],
          cycleId: 205101,
          deductRate: 20,
          cycleRemainingAmount: 60,
          expireTime: Date.now() + 86400000,
        },
        {
          couponFamily: 'PNL_DEDUCT',
          couponTypes: ['TASK_PNL_DEDUCT'],
          cycleId: 205102,
          deductRate: 30,
          cycleRemainingAmount: 40,
          expireTime: Date.now() + 86400000,
        },
      ],
      amount: coupon?.amount,
      coin: coupon?.coin,
    };
  }
  if (userCouponId === 1001) {
    return { resultCode: 'INSUFFICIENT_SELF_FUNDS', amount: 50, coin: 'USDT', needRechargeAmount: '101.51515152' };
  }
  if (userCouponId === 1003) return { resultCode: 'HAS_POSITION_OR_ORDER' };
  if (userCouponId === 1008) return { resultCode: 'PROCESSING' };
  // 仓位体验券：去使用 → 跳合约体验券模式
  if (coupon?.couponType === 'POSITION_EXPERIENCE' || coupon?.couponFamily === 'POSITION_EXPERIENCE') {
    return {
      resultCode: 'POSITION_USE_REQUIRED',
      userCouponId,
      symbolId: 1,
      amount: coupon?.amount,
      coin: coupon?.coin,
    };
  }
  if (userCouponId === 1005) {
    return {
      resultCode: 'POSITION_USE_REQUIRED',
      userCouponId,
      symbolId: 1,
      amount: coupon?.amount,
      coin: coupon?.coin,
    };
  }
  return {
    resultCode: 'SUCCESS',
    amount: coupon?.amount || 10,
    coin: coupon?.coin || 'USDT',
    userCouponId,
  };
};

export const getMockRewardHistory = (couponType?: CouponFilterType): RewardHistoryItem[] => {
  const types: CouponType[] = [
    'EXPERIENCE',
    'FEE_DEDUCT',
    'PNL_DEDUCT',
    'TOKEN',
    'POSITION_EXPERIENCE',
  ];
  const list = Array.from({ length: 12 }, (_, index) => {
    const type = types[index % types.length];
    return {
      coin: type === 'TOKEN' ? (index % 2 === 0 ? 'SOL' : 'LTC') : 'USDT',
      amount: type === 'TOKEN' ? 2 : [20, 10, 50, 5][index % 4],
      couponType: type,
      couponFamilyDesc: i18n.t(couponTypeKey(type)),
      receiveTime: moment()
        .subtract(index * 3, 'days')
        .valueOf(),
      issueTime: moment()
        .subtract(index * 3, 'days')
        .valueOf(),
      // 存枚举码，展示时由 getRewardSourceLabel 做 i18n
      rewardSource: 'SYSTEM',
    };
  });
  if (!couponType || couponType === 'ALL') return list;
  return list.filter(item => item.couponType === couponType);
};

const MOCK_EP_PRICE = 62000;

const mockEpMetrics = (amount: string | number, leverage: number, side: 'LONG' | 'SHORT' = 'LONG') => {
  const principal = Number(amount) || 0;
  const qty = leverage > 0 ? (principal * leverage) / MOCK_EP_PRICE : 0;
  const liq =
    side === 'LONG' ? MOCK_EP_PRICE * (1 - 1 / Math.max(leverage, 1)) : MOCK_EP_PRICE * (1 + 1 / Math.max(leverage, 1));
  return {
    estimatedQuantity: qty.toFixed(6),
    estimatedLiquidationPrice: liq.toFixed(2),
    nominalValue: String(principal * leverage),
  };
};

export const getMockExperienceCouponList = (): ExperienceCouponOption[] => MOCK_POSITION_EXPERIENCE_COUPONS;

export const getMockExperienceOrderPage = (
  symbolId = 1,
  userCouponId = 1011,
): ExperienceOrderPage => {
  const coupons = getMockExperienceCouponList();
  const coupon = coupons.find(item => item.userCouponId === userCouponId) || coupons[0];
  const metrics = mockEpMetrics(coupon.amount, coupon.leverage);
  const totalAmount = String(coupons.reduce((sum, item) => sum + Number(item.amount || 0), 0));
  return {
    symbolId,
    symbol: 'BTCUSDT',
    positionType: 'ISOLATED',
    positionModel: 'AGGREGATION',
    orderType: 'MARKET',
    contractSize: 0.001,
    minQty: 1,
    minNotional: 5,
    maxNotional: 1000000,
    pricePrecision: 1,
    latestPrice: String(MOCK_EP_PRICE),
    markPrice: '61980',
    priceTime: Date.now(),
    couponSummary: {
      totalAmount,
      couponCount: coupons.length,
      currentSymbolCouponCount: coupons.length,
    },
    selectedCoupon: coupon
      ? {
          ...coupon,
          positionValidDays: Math.max(1, Math.round(Number(coupon.positionValidHours || 24) / 24)),
        }
      : null,
    activePositionExists: false,
    supportsTakeProfit: true,
    supportsStopLoss: true,
    supportsPartialClose: false,
    supportsMarginAdjust: false,
    canOpen: Boolean(coupon),
    checkCode: coupon ? 'SUCCESS' : 'COUPON_UNAVAILABLE',
    // 兼容扁平字段
    userCouponId: coupon?.userCouponId,
    leverage: coupon?.leverage,
    principalAmount: coupon?.amount,
    positionValidHours: coupon?.positionValidHours,
    expireTime: coupon?.expireTime,
    symbols: coupon?.symbols,
    totalAmount,
    couponCount: coupons.length,
    currentSymbolCouponCount: coupons.length,
    estimatedLiquidationPrice: metrics.estimatedLiquidationPrice,
    estimatedQuantity: metrics.estimatedQuantity,
    resultCode: coupon ? 'SUCCESS' : 'COUPON_UNAVAILABLE',
  };
};

export const getMockExperiencePreview = (data: {
  symbolId: number;
  userCouponId: number;
  positionSide: 'LONG' | 'SHORT';
  takeProfitPrice?: number;
  stopLossPrice?: number;
}): ExperienceOpenPreview => {
  const coupons = getMockExperienceCouponList();
  const coupon = coupons.find(item => item.userCouponId === data.userCouponId) || coupons[0];
  const metrics = mockEpMetrics(coupon.amount, coupon.leverage, data.positionSide);
  return {
    symbolId: data.symbolId,
    symbol: 'BTCUSDT',
    userCouponId: coupon.userCouponId,
    positionSide: data.positionSide,
    positionType: 'ISOLATED',
    positionModel: 'AGGREGATION',
    orderType: 'MARKET',
    leverage: coupon.leverage,
    principalAmount: coupon.amount,
    estimatedQuantity: metrics.estimatedQuantity,
    nominalValue: metrics.nominalValue,
    latestPrice: String(MOCK_EP_PRICE),
    markPrice: '61980',
    priceTime: Date.now(),
    estimatedLiquidationPrice: metrics.estimatedLiquidationPrice,
    takeProfitPrice: data.takeProfitPrice ?? null,
    stopLossPrice: data.stopLossPrice ?? null,
    canOpen: true,
    resultCode: 'SUCCESS',
  };
};

export const getMockExperienceOpen = (): ExperienceOpenResult => ({
  opened: true,
  resultCode: 'SUCCESS',
  positionId: 980001,
  openOrderId: 970001,
  expireTime: Date.now() + 24 * 3600 * 1000,
  idempotentReplay: false,
});
